import { stripSoftHyphens } from "./soft-hyphens";
import type { JSX } from "react";
import type {
	IApiContentResponseModel,
	SchemaUmbPageCompositionContentResponseModel,
	SeoGeneralUmbPageCompositionContentModel,
	SharingGeneralUmbPageCompositionContentModel,
	SiteSettingsUmbPageContentResponseModel,
} from "#/_generated/umbraco/delivery-api/models";
import { getUmbracoCmsBaseUrlEnv } from "#/_packages/umbrella/config/utils.ts";
import { convertToCroppedFocalPointUrl } from "#/_packages/umbrella/integrations/umbraco/utils/image-utils.ts";

export function canonicalLinks(
	content: IApiContentResponseModel | undefined,
	siteOrigin: string,
) {
	if (!content) return [];
	const seoContent = content as SeoGeneralUmbPageCompositionContentModel;
	const paths = [
		seoContent.properties?.canonicalUrl?.route?.path,
		content.route?.path,
	];

	for (const path of paths) {
		if (!path?.trim()) continue;
		try {
			const url = new URL(path, siteOrigin);
			if (url.protocol !== "http:" && url.protocol !== "https:") continue;
			url.search = "";
			url.hash = "";
			return [{ rel: "canonical", href: url.href }];
		} catch {
			// Fall back to the current page if the configured target is invalid.
		}
	}
	return [];
}

export function languageAlternateLinks(
	content: IApiContentResponseModel | undefined,
	siteOrigin: string,
	defaultCulture?: string,
) {
	const links: Array<{ rel: string; hrefLang: string; href: string }> = [];
	for (const [culture, route] of Object.entries(content?.cultures ?? {})) {
		if (!culture.trim() || !route?.path?.trim()) continue;
		try {
			const url = new URL(route.path, siteOrigin);
			if (url.protocol !== "http:" && url.protocol !== "https:") continue;
			url.search = "";
			url.hash = "";
			links.push({
				rel: "alternate",
				hrefLang: culture.toLowerCase(),
				href: url.href,
			});
		} catch {
			// Unavailable or invalid language routes must not advertise an alternate.
		}
	}
	const defaultLink = links.find(
		(link) => link.hrefLang === defaultCulture?.toLowerCase(),
	);
	if (defaultLink) {
		links.push({ ...defaultLink, hrefLang: "x-default" });
	}
	return links;
}

function firstText(...values: Array<string | null | undefined>) {
	return stripSoftHyphens(values.find((value) => value?.trim())?.trim());
}

function pageTitle(
	content?: IApiContentResponseModel,
	settings?: SiteSettingsUmbPageContentResponseModel,
	sharing = false,
) {
	const seo = content as SeoGeneralUmbPageCompositionContentModel | undefined;
	const share = content as
		| SharingGeneralUmbPageCompositionContentModel
		| undefined;
	const title = firstText(
		sharing ? share?.properties?.shareTitle : undefined,
		seo?.properties?.metaTitle,
		content?.name,
	);
	const siteName = stripSoftHyphens(settings?.properties?.metaSiteName?.trim());
	return siteName
		? [title, settings?.properties?.metaTitleSeparator?.trim(), siteName]
				.filter(Boolean)
				.join(" ")
		: title;
}

function pageUrl(
	content: IApiContentResponseModel | undefined,
	siteOrigin?: string,
) {
	if (!content?.route?.path || !siteOrigin) return "";
	try {
		const url = new URL(content.route.path, siteOrigin);
		if (!["http:", "https:"].includes(url.protocol)) return "";
		url.search = "";
		url.hash = "";
		return url.href;
	} catch {
		return "";
	}
}

export function siteMetaData(
	settings?: SiteSettingsUmbPageContentResponseModel,
) {
	const properties = settings?.properties;
	return [
		{ name: "owner", content: properties?.metaOwner },
		{ name: "copyright", content: properties?.metaCopyright },
		{ name: "reply-to", content: properties?.metaEmail },
		{
			name: "google-site-verification",
			content: properties?.metaGoogleSiteVerification,
		},
		{
			name: "apple-mobile-web-app-title",
			content: properties?.appleMobileWebAppTitle,
		},
	].flatMap((meta) =>
		meta.content?.trim() ? [{ ...meta, content: meta.content.trim() }] : [],
	);
}

export function baseMetaData(
	content?: IApiContentResponseModel,
	siteSettings?: SiteSettingsUmbPageContentResponseModel,
	siteOrigin?: string,
) {
	const seo = content as SeoGeneralUmbPageCompositionContentModel | undefined;
	const hidden =
		seo?.properties?.hideContentFromSearchEngines ||
		["loginUmbPage", "restrictedAccessUmbPage"].includes(
			content?.contentType ?? "",
		);
	const url = pageUrl(content, siteOrigin);
	return [
		{ title: pageTitle(content, siteSettings) },
		{
			name: "description",
			content: firstText(
				seo?.properties?.metaDescription,
				siteSettings?.properties?.metaDescriptionFallback,
			),
		},
		{ name: "robots", content: hidden ? "noindex, nofollow" : "index, follow" },
		...(url ? [{ property: "url", content: url }] : []),
	];
}

export function openGraphMetaData(
	content?: IApiContentResponseModel,
	siteSettings?: SiteSettingsUmbPageContentResponseModel,
	siteOrigin?: string,
) {
	const seo = content as SeoGeneralUmbPageCompositionContentModel | undefined;
	const sharing = content as
		| SharingGeneralUmbPageCompositionContentModel
		| undefined;
	return [
		{ property: "og:title", content: pageTitle(content, siteSettings, true) },
		{
			property: "og:description",
			content: firstText(
				sharing?.properties?.shareDescription,
				seo?.properties?.metaDescription,
				siteSettings?.properties?.shareDescriptionFallback,
				siteSettings?.properties?.metaDescriptionFallback,
			),
		},
		{ property: "og:type", content: "website" },
		...[
			{ property: "og:url", content: pageUrl(content, siteOrigin) },
			{ property: "og:image", content: getSharingImage(content, siteSettings) },
			{
				property: "og:site_name",
				content: siteSettings?.properties?.metaSiteName,
			},
			{ property: "og:email", content: siteSettings?.properties?.metaEmail },
		].flatMap((meta) =>
			meta.content?.trim() ? [{ ...meta, content: meta.content.trim() }] : [],
		),
	];
}

export function twitterMetaData(
	content?: IApiContentResponseModel,
	siteSettings?: SiteSettingsUmbPageContentResponseModel,
) {
	const social = openGraphMetaData(content, siteSettings);
	return [
		{
			name: "twitter:card",
			content: getSharingImage(content, siteSettings)
				? "summary_large_image"
				: "summary",
		},
		...social
			.filter((meta) =>
				["og:title", "og:description", "og:image"].includes(meta.property),
			)
			.map((meta) => ({
				name: meta.property.replace("og:", "twitter:"),
				content: meta.content,
			})),
	];
}

function getSharingImage(
	content?: IApiContentResponseModel,
	settings?: SiteSettingsUmbPageContentResponseModel,
) {
	const sharing = content as
		| SharingGeneralUmbPageCompositionContentModel
		| undefined;
	const image =
		sharing?.properties?.shareImage?.[0] ??
		settings?.properties?.shareImageFallback?.[0];
	return image
		? convertToCroppedFocalPointUrl(image, { width: 1200, height: 630 })
		: undefined;
}

export const getThemeCssFromSiteSettings = (
	settings?: SiteSettingsUmbPageContentResponseModel,
) => {
	if (!settings?.properties?.themeToggle) return [];

	const themeCss = settings.properties.cssThemeVariables?.trim();
	if (!themeCss) return [];

	const styles: Array<JSX.IntrinsicElements["style"]> = [
		{
			id: "global-theme-variables",
			children: themeCss.includes("{") ? themeCss : `:root { ${themeCss} }`,
		},
	];

	return styles;
};

export const getScriptsInHeadFromSiteSettings = (
	settings?: SiteSettingsUmbPageContentResponseModel,
) => {
	if (!settings?.properties?.themeToggle) return [];

	const scripts = settings.properties.globalCustomScriptsInHead?.trim();
	if (!scripts) return [];

	return scripts;
};

export const getFontsFromSiteSettings = (
	_settings?: SiteSettingsUmbPageContentResponseModel,
): Array<JSX.IntrinsicElements["link"]> => [
	{ rel: "preconnect", href: "https://fonts.googleapis.com" },
	{
		rel: "preconnect",
		href: "https://fonts.gstatic.com",
		crossOrigin: "anonymous",
	},
];

function iconUrl(value?: string | null) {
	if (!value?.trim()) return undefined;
	let path = value.trim();
	if (path.startsWith("{")) {
		try {
			const crop: unknown = JSON.parse(path);
			if (
				!crop ||
				typeof crop !== "object" ||
				!("src" in crop) ||
				typeof crop.src !== "string"
			)
				return undefined;
			path = crop.src;
		} catch {
			return undefined;
		}
	}
	try {
		const url = new URL(path, getUmbracoCmsBaseUrlEnv());
		return ["http:", "https:"].includes(url.protocol) ? url.href : undefined;
	} catch {
		return undefined;
	}
}

function manifestUrl(value?: string | null) {
	const href = iconUrl(value);
	if (!href) return undefined;
	try {
		const url = new URL(href);
		const cms = new URL(getUmbracoCmsBaseUrlEnv() ?? "");
		if (url.origin === cms.origin && url.pathname.startsWith("/media/")) {
			return `${url.pathname}${url.search}${url.hash}`;
		}
	} catch {
		/* An absolute external URL can still work without a CMS origin. */
	}
	return href;
}

export const getFavIconsFromSiteSettings = (
	settings?: SiteSettingsUmbPageContentResponseModel,
) => {
	const properties = settings?.properties;
	return [
		{
			rel: "icon",
			href: iconUrl(properties?.faviconSvg),
			type: "image/svg+xml",
			sizes: "any",
		},
		{
			rel: "icon",
			href: iconUrl(properties?.favicon96),
			type: "image/png",
			sizes: "96x96",
		},
		{
			rel: "icon",
			href: iconUrl(properties?.faviconIco),
			type: "image/x-icon",
			sizes: "48x48",
		},
		{
			rel: "apple-touch-icon",
			href: iconUrl(properties?.appleTouchIcon),
			sizes: "180x180",
		},
		{ rel: "manifest", href: manifestUrl(properties?.siteWebmanifest) },
	].flatMap((link) => (link.href ? [{ ...link, href: link.href }] : []));
};

export const getDefaultUmbrellaCss = () => {
	const links: Array<Record<string, string>> = [];
	const baseUrl = getUmbracoCmsBaseUrlEnv();

	links.push({
		rel: "stylesheet",
		href: `${baseUrl}/umbrella/ui/cit-umbrella-core.css`,
	});

	links.push({
		rel: "stylesheet",
		href: `${baseUrl}/umbrella/ui/cit-umbrella-blockStyles.css`,
	});

	links.push({
		rel: "stylesheet",
		href: `${baseUrl}/umbrella/ui/cit-umbrella-umb-variables-base.css`,
	});

	return links;
};

export function structuredData(
	content?: IApiContentResponseModel,
	siteSettings?: SiteSettingsUmbPageContentResponseModel,
	siteOrigin?: string,
) {
	if (!content) return [];
	const schemaContent = content as
		| SchemaUmbPageCompositionContentResponseModel
		| undefined;
	const schemaCode = schemaContent?.properties?.schemaCode?.trim();
	const schemas = schemaCode ? parseSchemaCode(schemaCode) : [];
	if (schemas.length === 0) {
		const fallback = getOrganizationStructuredData(
			content,
			siteSettings,
			siteOrigin,
		);
		if (fallback) schemas.push(fallback);
	}
	return schemas.map((schema) => ({
		type: "application/ld+json",
		// Script content is raw HTML: escape '<' so data cannot close the element.
		children: JSON.stringify(schema, (_key, value) =>
			typeof value === "string" ? stripSoftHyphens(value) : value,
		).replace(/</g, "\\u003c"),
	}));
}

function getOrganizationStructuredData(
	content?: IApiContentResponseModel,
	siteSettings?: SiteSettingsUmbPageContentResponseModel,
	siteOrigin?: string,
) {
	const name = siteSettings?.properties?.metaSiteName?.trim();
	if (!name || !content?.route?.path || !siteOrigin) return undefined;
	let pageUrl: URL;
	try {
		pageUrl = new URL(content.route.path, siteOrigin);
		if (pageUrl.protocol !== "http:" && pageUrl.protocol !== "https:")
			return undefined;
		pageUrl.search = "";
		pageUrl.hash = "";
	} catch {
		return undefined;
	}

	const seoContent = content as SeoGeneralUmbPageCompositionContentModel;
	const organizationSchema = {
		"@context": "https://schema.org",
		"@type": "Organization",
		name,
		url: pageUrl.href,
		description:
			seoContent?.properties?.metaDescription ??
			siteSettings?.properties?.metaDescriptionFallback ??
			"",
	};

	return organizationSchema;
}

function parseSchemaCode(schemaCode: string): object[] {
	const parse = (value: string): object | undefined => {
		try {
			const parsed: unknown = JSON.parse(value);
			if (
				parsed &&
				typeof parsed === "object" &&
				(!Array.isArray(parsed) ||
					(parsed.length > 0 &&
						parsed.every((item) => item && typeof item === "object")))
			) {
				return parsed;
			}
		} catch {
			// Invalid CMS JSON is omitted; a valid Organization fallback may be used.
		}
		return undefined;
	};
	const rawJson = parse(schemaCode);
	if (rawJson) return [rawJson];
	const scriptPattern =
		/<script\b(?=[^>]*\btype\s*=\s*["']application\/ld\+json["'])[^>]*>([\s\S]*?)<\/script\s*>/gi;
	return [...schemaCode.matchAll(scriptPattern)].flatMap((match) => {
		const parsed = parse(match[1]);
		return parsed ? [parsed] : [];
	});
}
